import { useState, useEffect, useRef } from 'react';

const CHUNK_SIZE = 3; // Batch chars for fewer re-renders
const DEFAULT_INTERVAL = 35; // ms per chunk (~similar speed, better perf)

interface TypewriterTextProps {
  text: string;
  speed?: number;
  onComplete?: () => void;
  className?: string;
}

export function TypewriterText({
  text,
  speed = DEFAULT_INTERVAL,
  onComplete,
  className = '',
}: TypewriterTextProps) {
  const [displayedText, setDisplayedText] = useState('');
  const [isComplete, setIsComplete] = useState(false);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  useEffect(() => {
    if (!text) {
      onCompleteRef.current?.();
      return;
    }

    setDisplayedText('');
    setIsComplete(false);

    let i = 0;
    const interval = setInterval(() => {
      i = Math.min(i + CHUNK_SIZE, text.length);
      setDisplayedText(text.slice(0, i));
      if (i >= text.length) {
        clearInterval(interval);
        setIsComplete(true);
        onCompleteRef.current?.();
      }
    }, speed);

    return () => clearInterval(interval);
  }, [text, speed]);

  return (
    <span className={className}>
      {displayedText}
      {!isComplete && <span className="animate-pulse opacity-80">|</span>}
    </span>
  );
}
